import { Box, Stack } from '@mui/material';
import { DocumentNode } from 'graphql';

import { Button } from '@/components/form/baseInputs/Button';
import { DeleteButton } from '@/components/form/baseInputs/Button/DeleteButton';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import {
  FormProvider,
  useForm,
  useFormContext,
  useSubmitSuccessHandler,
} from '@/components/react-hook-form';
import { shouldRefetchQuery } from '@/graphql/client.utils';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { useReportError } from '@/hooks/useReportError';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { FormSideDrawer } from '@/modules/common/SideDrawer/FormSideDrawer';

import { GetDispositiveProvisionsDocument } from '../dispositiveProvisions/DispositiveProvisionsForm/graphql/GetDispositiveProvisions.generated';
import { ClientOrganizationForm } from './ClientOrganizationForm';
import {
  ClientOrganizationFormShape,
  defaultValues,
} from './ClientOrganizationModal.constants';
import {
  mapFormDataToUpdateInput,
  useSyncClientOrganizationDataToForm,
} from './EditClientOrganizationModal.utils';
import {
  useDeleteClientOrganizationMutation,
  useUpdateClientOrganizationForModaMutation,
} from './graphql/ClientOrganizationModal.generated';

export interface EditClientOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  householdId: string;
  ignoredQueryDocuments?: DocumentNode[];
  /** Called after a successful save or delete (not on cancel/close). */
  onAfterSave?: () => void;
  /** Called when the organization was removed before the editor loaded. */
  onNotFound?: () => void;
}

interface EditClientOrganizationModalInnerProps
  extends EditClientOrganizationModalProps {
  heading?: string;
}

function EditClientOrganizationModalInner({
  onClose,
  heading = 'Edit organization',
  organizationId,
  householdId,
  ignoredQueryDocuments,
  onAfterSave,
}: EditClientOrganizationModalInnerProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { formRef } = useFormSaveHandler();
  const canEdit = useCanEditHouseholdData();
  const { reset, handleSubmit, shouldBlockNavigation, formState } =
    useFormContext<ClientOrganizationFormShape>();

  const [updateOrganization, { loading: updateLoading }] =
    useUpdateClientOrganizationForModaMutation({
      refetchQueries: 'active',
      onError: (error) => {
        showFeedback(FeedbackMessages.formSaveError);
        reportError('Could not update client organization', error);
      },
    });

  const [deleteOrganization, { loading: deleteLoading }] =
    useDeleteClientOrganizationMutation({
      refetchQueries: 'active',
      onQueryUpdated: (query) => {
        return shouldRefetchQuery(query.queryName, {
          ignoredQueryDocuments: [
            GetDispositiveProvisionsDocument,
            ...(ignoredQueryDocuments ?? []),
          ],
          ignoredWhen: () => {
            return query.options.variables?.id === organizationId;
          },
        });
      },
      onError: (error) => {
        showFeedback(
          'Sorry, we failed to delete this organization. Please try again.'
        );
        reportError('Could not delete client organization', error);
      },
    });

  const handleDelete = async () => {
    if (!organizationId) {
      throw new Error('Cannot delete organization without an id!');
    }
    await deleteOrganization({ variables: { id: organizationId } });
    onAfterSave?.();
    closeDrawer();
  };

  const closeDrawer = () => {
    reset();
    onClose();
  };

  useSubmitSuccessHandler(() => {
    onAfterSave?.();
    closeDrawer();
  });

  const submitHandler = handleSubmit((formData) => {
    return updateOrganization({
      variables: {
        input: mapFormDataToUpdateInput(formData, organizationId, householdId),
      },
    });
  });

  return (
    <FormSideDrawer
      isOpen
      onClose={closeDrawer}
      heading={heading}
      shouldBlockClose={shouldBlockNavigation}
      data-testid="edit-client-organization-modal"
      body={
        <form
          ref={formRef}
          onSubmit={submitHandler}
          noValidate
          style={{ width: '100%' }}
        >
          <Box mt={2}>
            <ClientOrganizationForm />
          </Box>
        </form>
      }
      footer={
        <Stack direction="row" spacing={1} width="100%">
          {canEdit && (
            <DeleteButton
              loading={deleteLoading}
              onConfirmDelete={handleDelete}
            />
          )}
          <Box flexGrow={1} />
          <Button variant="secondary" size="sm" onClick={closeDrawer}>
            {canEdit ? 'Cancel' : 'Close'}
          </Button>
          {canEdit && (
            <Button
              data-testid="client-organization-modal-save"
              variant="primary"
              size="sm"
              onClick={submitHandler}
              loading={updateLoading || formState.isSubmitting}
            >
              Save changes
            </Button>
          )}
        </Stack>
      }
    />
  );
}

export function EditClientOrganizationModal(
  props: EditClientOrganizationModalProps
) {
  if (!props.isOpen) {
    return null;
  }

  return <EditClientOrganizationModalMounted {...props} />;
}

function EditClientOrganizationModalMounted({
  organizationId,
  onClose,
  onNotFound = onClose,
  ...props
}: EditClientOrganizationModalProps) {
  const formMethods = useForm<ClientOrganizationFormShape>({ defaultValues });
  useSyncClientOrganizationDataToForm(
    formMethods.reset,
    organizationId,
    onNotFound
  );

  return (
    <FormProvider {...formMethods}>
      <EditClientOrganizationModalInner
        organizationId={organizationId}
        onClose={onClose}
        {...props}
      />
    </FormProvider>
  );
}
